import { useState } from 'react';

import type { SocialPlatform, VenueDetail } from '../../../../api/venues';

import {
  buildSocialPatch,
  PLATFORM_LABELS,
  seedSocialDraft,
  type SocialDraft,
} from './social-form';
import type { SocialNotice } from './SocialPresenceView';
import { useFormDraft } from './use-form-draft';
import { useUpdateVenueMarketing } from './use-venue-marketing';
import { fieldErrorsFor, marketingErrorMessage } from './venue-marketing-errors';

const CHECK_FIELDS = 'Check the highlighted fields and try again.';

/**
 * State and saves for the social presence section. The form is reseeded only
 * when the venue's social itself changes. Keying it on `updatedAt` would throw
 * away an unsaved edit whenever anything else on the venue was saved.
 */
export function useSocialEditor(venue: VenueDetail) {
  const update = useUpdateVenueMarketing(venue.id);
  const { draft, setDraft, reset } = useFormDraft(
    seedSocialDraft(venue.social),
    JSON.stringify(venue.social),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<SocialNotice | null>(null);
  const regionName = venue.region?.name ?? null;

  const save = async () => {
    const built = buildSocialPatch(draft, venue.social);
    if (Object.keys(built.errors).length > 0) {
      setErrors(built.errors);
      throw new Error(CHECK_FIELDS);
    }
    if (Object.keys(built.patch).length === 0) return;
    try {
      await update.mutateAsync({ social: built.patch });
      setErrors({});
    } catch (err) {
      const fields = fieldErrorsFor(err);
      setErrors(fields);
      throw new Error(fields._ ?? marketingErrorMessage(err, 'save-social', regionName));
    }
  };

  const verify = async (platform: SocialPlatform) => {
    setNotice(null);
    try {
      await update.mutateAsync({ markVerified: [platform] });
      setNotice({ ok: true, text: `${PLATFORM_LABELS[platform]} marked verified` });
    } catch (err) {
      setNotice({ ok: false, text: marketingErrorMessage(err, 'verify', regionName) });
    }
  };

  const change = (next: SocialDraft) => {
    setErrors({});
    setNotice(null);
    setDraft(next);
  };

  const cancel = () => {
    setErrors({});
    reset();
  };

  return { draft, errors, notice, busy: update.isPending, save, verify, change, cancel };
}
